import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateRequirementRequest,
  JiraPreview,
  Requirement,
  UpdateRequirementRequest,
} from '../models/requirement.model';
import { RequirementActivity, RequirementActivityType } from '../models/requirement-activity.model';
import { ProviderType } from '../models/project-integration.model';
import { Page } from '../models/page.model';
import { SaveFullRequirementRequest, SaveFullRequirementResponse } from '../models/save-full-requirement.model';

/** Eine große Seite holen; {@code Page.last} signalisiert eine etwaige Kürzung. */
const ACTIVITY_PAGE_SIZE = 200;

@Injectable({ providedIn: 'root' })
export class RequirementsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/api/v1/projects`;

  list(projectId: string): Observable<Requirement[]> {
    return this.http.get<Requirement[]>(`${this.baseUrl}/${projectId}/requirements`);
  }

  getById(projectId: string, id: string): Observable<Requirement> {
    return this.http.get<Requirement>(`${this.baseUrl}/${projectId}/requirements/${id}`);
  }

  create(projectId: string, request: CreateRequirementRequest): Observable<Requirement> {
    return this.http.post<Requirement>(`${this.baseUrl}/${projectId}/requirements`, request);
  }

  update(projectId: string, id: string, request: UpdateRequirementRequest): Observable<Requirement> {
    return this.http.patch<Requirement>(`${this.baseUrl}/${projectId}/requirements/${id}`, request);
  }

  delete(projectId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/requirements/${id}`);
  }

  createFull(projectId: string, request: SaveFullRequirementRequest): Observable<SaveFullRequirementResponse> {
    return this.http.post<SaveFullRequirementResponse>(`${this.baseUrl}/${projectId}/requirements/full`, request);
  }

  saveFull(
    projectId: string,
    id: string,
    request: SaveFullRequirementRequest,
  ): Observable<SaveFullRequirementResponse> {
    return this.http.put<SaveFullRequirementResponse>(`${this.baseUrl}/${projectId}/requirements/${id}/full`, request);
  }

  refreshJira(projectId: string, id: string): Observable<Requirement> {
    return this.http.post<Requirement>(`${this.baseUrl}/${projectId}/requirements/${id}/jira/refresh`, null);
  }

  previewJira(projectId: string, key: string, provider?: ProviderType | null): Observable<JiraPreview> {
    let params = new HttpParams().set('key', key);
    if (provider) params = params.set('provider', provider);
    return this.http.get<JiraPreview>(`${this.baseUrl}/${projectId}/requirements/jira/preview`, { params });
  }

  /**
   * Änderungs-Historie einer Anforderung (NEX-492/NEX-494), sortiert nach
   * {@code createdAt desc}. Optionaler Typ-Filter wird an das BE durchgereicht.
   */
  listActivities(
    projectId: string,
    id: string,
    types?: RequirementActivityType[],
    size = ACTIVITY_PAGE_SIZE,
  ): Observable<Page<RequirementActivity>> {
    let params = new HttpParams().set('size', String(size));
    for (const type of types ?? []) {
      params = params.append('type', type);
    }
    return this.http.get<Page<RequirementActivity>>(`${this.baseUrl}/${projectId}/requirements/${id}/activities`, {
      params,
    });
  }

  /** CSV-Export der (optional gefilterten) Historie als Blob für den Download. */
  exportActivitiesCsv(projectId: string, id: string, types?: RequirementActivityType[]): Observable<Blob> {
    let params = new HttpParams();
    for (const type of types ?? []) {
      params = params.append('type', type);
    }
    return this.http.get(`${this.baseUrl}/${projectId}/requirements/${id}/activities.csv`, {
      params,
      responseType: 'blob',
    });
  }
}
